import {safeErrorCode} from "../features/agent/errors.mjs";
const BASE = "/api/v1/agent-control/v2";

async function req<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
    ...init,
  });
  if (!r.ok) {
    let detail =
      r.status === 401
        ? "authentication_required"
        : `request_failed_${r.status}`;
    try {
      const body = await r.json();
      detail = safeErrorCode(body, r.status);
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return r.json() as Promise<T>;
}

export interface V2Binding {
  workspace_cwd: string;
  mode: string;
  memory_scope: string;
}

export interface V2SessionSummary {
  id: string;
  title: string;
  status: string;
  created_at: string;
  updated_at: string;
  workspace_cwd: string;
  cline_session_id: string | null;
  worker_run_id: string | null;
  provider_key: string | null;
  model_key: string | null;
  preferred_model_key: string | null;
  execution_profile: string;
  execution_scope: "STANDARD" | "ISOLATED_LOCAL";
  mode: string;
  memory_scope: string;
  project_id: string | null;
  last_error: string | null;
  pinned_at: string | null;
  archived_at: string | null;
  deleted_at: string | null;
}

export interface V2ModelOption {
  id: string;
  context_window: number | null;
  upstream_model?: string | null;
  provider_key?: string | null;
}

/* KNSAIX_IZLOTASK_CLIENT_V1 */

export interface V2InputAttachment {
  id: string;
  name: string;
  mime_type: string;
  size: number;
  sha256: string;
  created_at: string;
}

export interface V2Artifact {
  path: string;
  name: string;
  mime_type: string;
  size: number;
  sha256: string;
  previewable: boolean;
  download_url: string;
  preview_url: string | null;
}

export interface V2Message {
  id: string;
  role: string;
  content: string;
  model_key: string | null;
  provider_key: string | null;
  created_at: string;
  artifacts?: V2Artifact[];
  attachments?: V2InputAttachment[];
}

export interface V2TaskRequirement {
  key: string;
  description: string;
  status: string;
  materiality?: string;
  blocking_scope?: string | null;
  value?: unknown;
  created_at?: string | null;
  updated_at?: string | null;
  decision_required_at?: string | null;
  resolved_at?: string | null;
  conflicting_at?: string | null;
  unavailable_at?: string | null;
}

export interface V2ResearchRequest {
  requirement_key: string;
  capability?: string;
  query?: string;
  preferred_provider?: string;
  source_policy?: string;
  materiality?: string;
  fallback?: string;
  queued_at?: string | null;
  updated_at?: string | null;
}

export interface V2ResearchEvidence {
  requirement_key?: string;
  status?: string;
  value?: unknown;
  summary?: unknown;
  source_url?: string | null;
  source_authority?: string | null;
  confidence?: number | null;
  retrieved_at?: string | null;
  projected_at?: string | null;
  conflict_note?: string | null;
  applied?: boolean;
}

export interface V2TaskIntelligence {
  schema: string;
  revision?: number;
  archetype?: string;
  readiness?: number;
  created_at?: string | null;
  updated_at?: string | null;
  requirements?: V2TaskRequirement[];
  assumptions?: Array<Record<string, unknown>>;
  blocking_requirement_keys?: string[];
  decision_requirement_keys?: string[];
  discoverable_requirement_keys?: string[];
  research_required?: boolean;
  research_queue?: V2ResearchRequest[];
  research_resolution?: {
    schema?: string;
    projected_at?: string | null;
    items?: V2ResearchEvidence[];
  } | null;
  user_decision_resolution?: {
    schema?: string;
    resolved_at?: string | null;
    resolved_decisions?: Record<string, unknown>;
  } | null;
}

export interface V2TaskExecutableSpec {
  schema?: string;
  generated_at?: string | null;
  readiness?: number;
  runnable?: number[];
  blocked?: number[];
  work_packages?: Array<{
    index?: number;
    key?: string;
    goal?: string;
    task_intelligence_status?: string;
    depends_on_decisions?: string[];
    projected_at?: string | null;
  }>;
}

export interface V2RunState {
  status: string | null;
  worker_run_id: string | null;
  result_text: string | null;
  error_message: string | null;
  cline_session_id: string | null;
}

export interface V2SyncEvent {
  seq?: number;
  action_type?: string;
  description?: string;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return req<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

function patch<T>(path: string, body: unknown): Promise<T> {
  return req<T>(path, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export const agentControlV2Api = {
  listSessions: (
    view: "active" | "archived" = "active",
  ) =>
    req<V2SessionSummary[]>(
      `/sessions?view=${view}`,
    ),
  models: () =>
    req<V2ModelOption[]>("/models"),
  localModels: () =>
    req<V2ModelOption[]>("/local-models"),

  updatePreferences: (
    id: string,
    body: {
      preferred_model_key: string | null;
      execution_profile: string;
    },
  ) =>
    patch<{
      session_id: string;
      preferred_model_key: string | null;
      execution_profile: string;
    }>(
      `/sessions/${id}/preferences`,
      body,
    ),

  renameSession: (id: string, title: string) =>
    patch<{ session_id: string; title: string }>(
      `/sessions/${id}/title`,
      { title },
    ),
  pinSession: (id: string) =>
    post(`/sessions/${id}/pin`),
  unpinSession: (id: string) =>
    post(`/sessions/${id}/unpin`),
  archiveSession: (id: string) =>
    post(`/sessions/${id}/archive`),
  restoreSession: (id: string) =>
    post(`/sessions/${id}/restore`),
  deleteSession: (id: string) =>
    req(`/sessions/${id}`, {
      method: "DELETE",
    }),
  deleteMessage: (sessionId: string, messageId: string) =>
    req<{ message_id: string; deleted: boolean }>(
      `/sessions/${sessionId}/messages/${messageId}`,
      { method: "DELETE" },
    ),
  createSession: (body: {
    title: string;
    workspace_cwd: string;
    memory_scope: string;
    mode: string;
    preferred_model_key?: string | null;
    execution_profile?: string;
    execution_scope?: "STANDARD" | "ISOLATED_LOCAL";
  }) => post<{ session_id: string }>("/sessions", body),
  getSession: (id: string) =>
    req<{
      session: unknown;
      messages: V2Message[];
      run: V2RunState;
      task_intelligence: V2TaskIntelligence | null;
      task_execution_status: string | null;
      task_decision_dependencies: Record<string, unknown> | null;
      task_executable_spec: V2TaskExecutableSpec | null;
      task_work_item_id: string | null;
      task_state_version: number | null;
      resume_semantics: string;
    }>(`/sessions/${id}`),
  pendingAttachments: (id: string) =>
    req<V2InputAttachment[]>(`/sessions/${id}/attachments`),

  uploadAttachment: (id: string, file: File) =>
    req<V2InputAttachment>(
      `/sessions/${id}/attachments?name=${encodeURIComponent(file.name)}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      },
    ),

  deleteAttachment: (id: string, attachmentId: string) =>
    req<{ deleted: boolean }>(
      `/sessions/${id}/attachments/${attachmentId}`,
      { method: "DELETE" },
    ),

  submitMessage: (
    id: string,
    content: string,
    resume = 0,
    attachmentIds: string[] = [],
  ) =>
    // KNSAIX_MESSAGE_ADMISSION_V1
    post<{
      admission: "CHAT" | "TASK";
      execution_required: boolean;
      task_id: string | null;
      status: string;
      intent?: string;
      confidence?: number;
      user_message_id?: string;
      assistant_message_id?: string;
      model_key?: string | null;
      provider_key?: string | null;
    }>(
      `/sessions/${id}/messages?resume=${resume}`,
      { content, attachment_ids: attachmentIds },
    ),
  cancel: (id: string) =>
    post<{ cancelled: boolean }>(`/sessions/${id}/cancel`),
  sync: (id: string, since = 0) =>
    req<{
      status: string;
      events: V2SyncEvent[];
      latest_seq: number;
      result_text?: string;
      error_message?: string;
    }>(
      `/sessions/${id}/sync?since=${since}`,
    ),
  trace: (id: string) => req<unknown[]>(`/sessions/${id}/trace`),
  approvals: () => req<unknown[]>("/approvals"),
  toolchain: () => req<Record<string, unknown>>("/toolchain"),
};

// KNSAIX_P013M_ACTIVE_CORRECTION_V1
export async function queueAgentControlCorrection(
  sessionId: string,
  content: string,
): Promise<{
  accepted: boolean;
  session_id: string;
  work_item_id: string;
  message_id?: string;
  event_id?: string;
  status: string;
  message: string;
}> {
  const response = await fetch(`/api/v1/agent-control/v2/sessions/${encodeURIComponent(sessionId)}/corrections`, {
    method: "POST",
    credentials: "include",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify({content}),
  });
  if (!response.ok) {
    let code = "correction_failed";
    try {
      const data = await response.json();
      code = data?.detail || data?.code || code;
    } catch {}
    throw new Error(String(code));
  }
  return await response.json();
}

